/**
 * WhatWillHappen — landing-page section showcasing the illustrated
 * "consequences" story. Compact layout: 4 slides visible at once on desktop.
 * Swipe / drag / arrows / dots / auto-advance every 5s (pauses on hover).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";

const FALLBACK_SLIDES = Array.from({ length: 22 }, (_, i) => ({
    id: `wwh-${i + 1}`,
    order: i + 1,
    image_url: `/what-will-happen/${String(i + 1).padStart(2, "0")}.webp`,
    alt: `What will happen — slide ${i + 1}`,
}));

const GAP = 16;
const AUTO_MS = 5000;

export function WhatWillHappen() {
    const [slides] = useState(FALLBACK_SLIDES);
    const [activeIndex, setActiveIndex] = useState(0);
    const [dragging, setDragging] = useState(false);
    const { t } = useLang();
    const scrollerRef = useRef(null);

    const cardStep = () => {
        const scroller = scrollerRef.current;
        const card = scroller?.querySelector("[data-slide-index]");
        return (card?.getBoundingClientRect().width || 1) + GAP;
    };

    /* Track active slide from scroll position (first fully-visible card) */
    const onScroll = useCallback(() => {
        const scroller = scrollerRef.current;
        if (!scroller) return;
        const idx = Math.round(scroller.scrollLeft / cardStep());
        setActiveIndex((cur) => {
            const clamped = Math.max(0, Math.min(slides.length - 1, idx));
            return clamped === cur ? cur : clamped;
        });
    }, [slides.length]);

    const scrollToIndex = useCallback((idx, behavior = "smooth") => {
        const scroller = scrollerRef.current;
        if (!scroller) return;
        const card = scroller.querySelector(`[data-slide-index="${idx}"]`);
        if (!card) return;
        scroller.scrollTo({ left: Math.max(0, card.offsetLeft - GAP), behavior });
    }, []);

    const next = useCallback(() => {
        setActiveIndex((cur) => {
            const nxt = (cur + 1) % slides.length; // loop
            scrollToIndex(nxt);
            return nxt;
        });
    }, [slides.length, scrollToIndex]);

    const prev = useCallback(() => {
        setActiveIndex((cur) => {
            const nxt = (cur - 1 + slides.length) % slides.length; // loop
            scrollToIndex(nxt);
            return nxt;
        });
    }, [slides.length, scrollToIndex]);

    /* Auto-swipe every 5s — always on, no hover pause. Only stops while user is actively dragging. */
    useEffect(() => {
        if (dragging || slides.length < 2) return undefined;
        const timer = setInterval(next, AUTO_MS);
        return () => clearInterval(timer);
    }, [dragging, slides.length, next]);

    /* Keyboard navigation */
    const onKeyDown = (e) => {
        if (e.key === "ArrowRight") { e.preventDefault(); next(); }
        else if (e.key === "ArrowLeft") { e.preventDefault(); prev(); }
    };

    /* Mouse-drag support (mimic swipe on desktop) */
    const drag = useMemo(() => ({ down: false, startX: 0, startScroll: 0 }), []);
    const onPointerDown = (e) => {
        const scroller = scrollerRef.current;
        if (!scroller) return;
        setDragging(true);
        drag.down = true;
        drag.startX = e.clientX;
        drag.startScroll = scroller.scrollLeft;
        scroller.setPointerCapture?.(e.pointerId);
        scroller.style.scrollSnapType = "none";
    };
    const onPointerMove = (e) => {
        if (!drag.down) return;
        const dx = e.clientX - drag.startX;
        scrollerRef.current.scrollLeft = drag.startScroll - dx;
    };
    const onPointerUp = (e) => {
        if (!drag.down) return;
        drag.down = false;
        setDragging(false);
        const scroller = scrollerRef.current;
        if (!scroller) return;
        scroller.releasePointerCapture?.(e.pointerId);
        scroller.style.scrollSnapType = "";
        const idx = Math.round(scroller.scrollLeft / cardStep());
        const clamped = Math.max(0, Math.min(slides.length - 1, idx));
        scrollToIndex(clamped);
    };

    return (
        <section
            id="what-will-happen"
            data-testid="landing-what-will-happen"
            aria-label="What will happen — story carousel"
            className="relative w-full bg-white overflow-hidden py-8 md:py-12"
            tabIndex={0}
            onKeyDown={onKeyDown}
        >
            {/* Editorial title */}
            <div className="w-full px-4 sm:px-6 lg:px-10 pb-4 md:pb-6 text-center">
                {t("wwhEyebrow") ? (
                    <p className="text-[11px] sm:text-[12px] uppercase tracking-[0.22em] font-bold text-[#EF4444] inline-flex items-center gap-2">
                        {t("wwhEyebrow")}
                    </p>
                ) : null}
                <h2 className="mx-auto mt-2.5 text-[24px] sm:text-[30px] md:text-[36px] lg:text-[38px] font-extrabold tracking-[-0.02em] text-[#6D28D9] leading-[1.12] md:whitespace-nowrap">
                    {t("wwhTitleA")}{" "}
                    <span className="text-[#6D28D9]">{t("wwhTitleB")}</span>
                </h2>
            </div>

            {/* Carousel — 3 slides visible on desktop, arrows on the sides */}
            <div className="relative max-w-6xl mx-auto px-4 sm:px-8 md:px-12">
                {/* Prev / Next buttons */}
                <button
                    type="button"
                    onClick={() => prev()}
                    data-testid="wwh-prev"
                    aria-label="Previous slide"
                    className="hidden md:flex absolute left-0 lg:-left-1 top-1/2 -translate-y-1/2 z-10 h-9 w-9 rounded-full bg-white border border-[#E5E7EB] shadow-[0_4px_14px_rgba(17,24,39,0.10)] items-center justify-center text-[#6B7280] hover:text-[#6D28D9] transition-all"
                >
                    <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                    type="button"
                    onClick={() => next()}
                    data-testid="wwh-next"
                    aria-label="Next slide"
                    className="hidden md:flex absolute right-0 lg:-right-1 top-1/2 -translate-y-1/2 z-10 h-9 w-9 rounded-full bg-white border border-[#E5E7EB] shadow-[0_4px_14px_rgba(17,24,39,0.10)] items-center justify-center text-[#6B7280] hover:text-[#6D28D9] transition-all"
                >
                    <ChevronRight className="h-5 w-5" />
                </button>

                {/* Scroll-snap swipeable track — 3-up on desktop */}
                <div
                    ref={scrollerRef}
                    data-testid="wwh-track"
                    onScroll={onScroll}
                    onPointerDown={onPointerDown}
                    onPointerMove={onPointerMove}
                    onPointerUp={onPointerUp}
                    onPointerCancel={onPointerUp}
                    className="flex gap-4 w-full overflow-x-auto snap-x snap-mandatory scroll-smooth cursor-grab active:cursor-grabbing select-none hide-scrollbar py-2"
                >
                    {slides.map((slide, idx) => (
                        <article
                            key={slide.id}
                            data-slide-index={idx}
                            data-testid={`wwh-slide-${idx}`}
                            className="snap-start shrink-0 w-[78vw] sm:w-[45%] md:w-[calc((100%-2rem)/3)] aspect-[3/2] rounded-xl overflow-hidden bg-[#F9FAFB] border border-[#EDEDF2] shadow-[0_10px_26px_rgba(17,24,39,0.10)] relative"
                        >
                            <img
                                src={slide.image_url}
                                alt={slide.alt || `Slide ${idx + 1}`}
                                draggable={false}
                                loading={idx < 5 ? "eager" : "lazy"}
                                className="absolute inset-0 w-full h-full object-contain pointer-events-none"
                            />
                            <div className="absolute bottom-2 left-2 z-10 inline-flex items-center gap-1.5 rounded-full bg-black/45 backdrop-blur text-white px-2 py-0.5 text-[10px] font-semibold tracking-wide">
                                {String(idx + 1).padStart(2, "0")} / {String(slides.length).padStart(2, "0")}
                            </div>
                        </article>
                    ))}
                </div>
            </div>
        </section>
    );
}

export default WhatWillHappen;
